<template>
  <div class="company-settings">
    <modal-edit-user v-if="displayEditUserModal" :user-to-edit="userToEdit" @close="displayEditUserModal = false" />

    <v-table
      class="company-table"
      :headers="headers"
      :items="list"
      :loading="loading"
      :table-name="tableName"
      @updateHeaders="updateHeaders"
    >
      <template #header="{ header }">
        <div>
          <template v-if="header.id === 'checkbox'">
            <vchecklabel v-model="isAllUsersSelected" class="vchecklabel-block" :disabled="isCurrentUserAdmin" />
          </template>

          <template v-else-if="header.id === 'avatar'">
            <div class="filter-wrapper">
              <div class="column_title" @click="setSortingMethod('name')">
                <i v-if="sorting.name === 'name'" class="fa" :class="sortingIcon"></i>
                {{ $tr('settings.managers.addUser.user') }}
              </div>

              <div class="column_filter" @click.stop="set_show_filter('name')">
                <icon-filter :color="temp_name ? 'green' : 'grey'" />
              </div>

              <vtfilter
                v-if="show_filter === 'name'"
                class="th_filter"
                type="text"
                :text_value="temp_name"
                @input="set_temp_name"
                @apply="update_term"
                @cancel="cancel_filter"
                @clear="name_clear"
              />
            </div>
          </template>

          <template v-else-if="header.id === 'rights'">
            <div class="column-rights">
              <div class="filter-wrapper">
                <div class="column_title">
                  {{ $tr('settings.managers.addUser.role') }}
                </div>

                <div class="column_filter" @click.stop="set_show_filter('role')">
                  <icon-filter color="grey" />
                </div>

                <vtfilter
                  v-if="show_filter === 'role'"
                  class="th_filter"
                  type="list"
                  :list="roles"
                  :selected_list="filter_temp_selected_roles"
                  :input_placeholder="$tr('settings.managers.addUser.role')"
                  filter_name="role"
                  @input="set_filter_temp_selected_roles"
                  @apply="apply_filter('role')"
                  @cancel="cancel_filter"
                  @clear="role_clear"
                />
              </div>
            </div>
          </template>

          <template v-else-if="header.id === 'email'">
            <div @click="setSortingMethod('email')">
              <i v-if="sorting.name === 'email'" class="fa" :class="sortingIcon"></i>

              {{ $tr('common.list.email') }}
            </div>
          </template>

          <template v-else-if="header.id === 'phone'">
            <div @click="setSortingMethod('phone')">
              <i v-if="sorting.name === 'phone'" class="fa" :class="sortingIcon"></i>

              {{ $tr('common.list.phone') }}
            </div>
          </template>

          <template v-else-if="header.id === 'region'">
            <div>{{ $tr('common.list.region') }}</div>
          </template>
        </div>
      </template>

      <template #cell="{ header, cell }">
        <user-row
          v-if="list.length"
          :model="cell"
          :header="header"
          :cell="cell"
          :current-user="currentUser"
          @clicked="editUser(cell)"
        />
      </template>
    </v-table>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import UserRow from './Row';
import ModalEditUser from './modals/EditUser';
import VTable from '@/components/table/index';

import { ADMIN_ROLES, ROLE_COMPANY_ADMIN } from '@/_helpers/constants.js';

export default {
  name: 'PreferencesAccessesList',

  components: {
    ModalEditUser,
    UserRow,
    VTable
  },

  props: {
    list: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      tableName: 'company-managers',
      headers: [
        {
          id: 'checkbox',
          name: '',
          is_visible: true,
          is_base: true,
          width: '60px'
        },
        {
          id: 'avatar',
          name: this.$tr('settings.managers.addUser.user'),
          is_visible: true,
          is_base: false,
          width: '170px'
        },
        {
          id: 'rights',
          name: this.$tr('settings.managers.addUser.role'),
          is_visible: true,
          is_base: false,
          width: '144px'
        },
        {
          id: 'email',
          name: this.$tr('common.list.email'),
          is_visible: true,
          is_base: false,
          width: '197px'
        },
        {
          id: 'phone',
          name: this.$tr('common.list.phone'),
          is_visible: true,
          is_base: false,
          width: '150px'
        },
        {
          id: 'region',
          name: this.$tr('common.list.region'),
          is_visible: true,
          is_base: false,
          width: '125px'
        }
      ],

      displayEditUserModal: false,
      userToEdit: {},
      show_filter: 'none',
      filter_temp_selected_roles: [],
      filter_loaded: false,
      isSelectedAllFilters: {},
      temp_name: ''
    };
  },

  computed: {
    ...mapState('users', ['loading']),

    roles() {
      return this.$store.getters['dictionary/userRoles'];
    },

    isCurrentUserAdmin() {
      let isAdmin = false;

      if (this.currentUser && this.list) {
        this.list.forEach(user => {
          const roles = user.roles && user.roles.length ? user.roles : [];

          if (
            (ADMIN_ROLES.some(role => this.has_role(role, roles)) || this.has_role(ROLE_COMPANY_ADMIN, roles)) &&
            user.email === this.currentUser.email
          ) {
            isAdmin = true;
          }
        });
      }

      return isAdmin;
    },

    isAllUsersSelected: {
      get() {
        if (!this.list.length) {
          return false;
        }
        return this.list.every(user => this.$store.getters['users/isSelected'](user));
      },

      set(state) {
        this.list.every(user => this.$store.dispatch('users/setSelectedState', { model: user, state }));
      }
    },

    currentUser() {
      return this.$store.getters['user/current'];
    },

    sorting() {
      return this.$store.state.users.sorting;
    },

    sortingIcon() {
      return {
        'fa-caret-down': this.sorting.order === 'desc',
        'fa-caret-up': this.sorting.order === 'asc'
      };
    }
  },

  beforeMount() {
    if (!this.currentUser || !this.currentUser.id) {
      this.fetch();
    }
  },

  methods: {
    updateHeaders(headers) {
      this.headers.splice(0, this.headers.length, ...headers);
    },

    editUser(user) {
      this.displayEditUserModal = true;
      this.userToEdit = user;
    },

    setSortingMethod(method) {
      this.$store.dispatch('users/updateSorting', {
        name: method,
        order: this.sorting.name !== method || this.sorting.order === 'desc' ? 'asc' : 'desc'
      });
    },

    has_role(needed, roles) {
      return roles.some(({ role }) => role === needed);
    },

    set_filter_temp_selected_roles(role) {
      this.filter_temp_selected_roles = role.result;
      if (role.params && role.params.name) {
        this.isSelectedAllFilters[role.params.name] = !!role.params.isSelectAll;
      }
    },

    apply_filter(filter) {
      if (this.isSelectedAllFilters[filter] && this[`${filter}_clear`]) {
        this[`${filter}_clear`]();
      }
      this.$store.dispatch('users/updateRoleId', this.filter_temp_selected_roles);
      this.set_show_filter('none');
    },

    cancel_filter() {
      this.set_show_filter('none');
    },

    role_clear() {
      this.filter_temp_selected_roles = [];
      this.$store.dispatch('users/updateRoleId', this.filter_temp_selected_roles);
      this.set_show_filter('none');
    },

    set_show_filter(value) {
      this.show_filter = value;
    },

    set_temp_name(name) {
      this.temp_name = name;
    },

    name_clear() {
      this.temp_name = '';
      this.set_show_filter('none');

      this.update_term();
    },

    delete_search_term() {
      this.temp_name = null;
      this.update_term();
    },

    update_term() {
      // console.log('update_term: ', this.temp_name)
      this.$store.dispatch('users/updateSearchTerm', this.temp_name).then(() => {
        this.fetchUsers();
        this.set_show_filter('none');
      });
    },

    fetch() {
      this.$store.dispatch('user/userInfoRequest', this.$store.getters['user/accessToken'].user.email);
    },

    fetchUsers() {
      this.$store.dispatch('users/fetchUsers');
    }
  }
};
</script>

<style lang="scss" scoped>
.company-table {
  margin-top: 15px;

  tr {
    height: auto;
  }

  .column-checkbox {
    width: 60px;
  }

  .filter-wrapper {
    display: inline-flex;
    position: relative;
    align-items: center;

    & .column_filter {
      margin-left: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    & .th_filter {
      position: absolute;
      left: 0;
      top: 30px;
    }
  }

  &::v-deep {
    .multiselect.big {
      max-width: 106px;
    }
  }
}

::v-deep .company-region {
  max-width: 100%;
}

.company-settings {
  &::v-deep {
    .vtable {
      th,
      td {
        word-break: break-word;
      }

      th {
        padding: 16px 12px 16px 0;
      }

      td {
        padding: 22px 12px 22px 24px;
      }

      .vchecklabel-block {
        width: 24px;
      }
    }
  }
}
</style>
